import { appConfig } from '../config/appConfig';
import { apiClient } from './apiClient';

export type FamilyPlanAdvantage = {
  key: string;
  title: string;
  body: string;
};

export type FamilyPlanSubject = {
  code: string;
  label: string;
  priceYuan?: string;
  amountFen?: number;
};

export type FamilyPlanCatalog = {
  productName: string;
  priceUnit: string;
  fluctuationNote: string;
  manifesto: string;
  eraQuestion: string;
  answerLead: string;
  advantages: FamilyPlanAdvantage[];
  advantagesFootnote: string;
  subjects: FamilyPlanSubject[];
};

export type FamilyPlanPricing = {
  pricingDate: string;
  priceUnit: string;
  fluctuationNote: string;
  subjects: Array<{
    code: string;
    label: string;
    priceYuan: string;
    amountFen: number;
  }>;
};

export type FamilyPlanPlanner = {
  userId: string;
  label: string;
  phoneMasked?: string;
};

export type FamilyPlanOrder = {
  id: string;
  plannerUserId: string;
  plannerLabel: string;
  subject: string;
  subjects: string[];
  subjectLabel: string;
  amountFen: number;
  status: string;
  createdAt?: string;
};

export type FamilyPlanPayResult = {
  success?: boolean;
  alreadyPaid?: boolean;
  orderId?: string;
  amountFen?: number;
  paymentStatus?: string;
  channel?: 'native' | 'jsapi';
  codeUrl?: string;
  payParams?: Record<string, string>;
};

function assertFactoryApiTarget(baseUrl: string): void {
  const base = String(baseUrl || '').trim();
  if (!base) throw new Error('未配置 AppFactory 基址（VITE_APP_FACTORY_BASE_URL）');
  if (/fcapp\.run/i.test(base) || /\.fc\.aliyuncs\.com/i.test(base)) {
    throw new Error('禁止家庭计划请求 Function Compute 地址；请指向 AppFactory');
  }
}

function extractMessage(payload: Record<string, unknown>, fallback: string): string {
  const message = String(payload.message || '').trim();
  if (message) return message;
  const error = String(payload.error || '').trim();
  if (error && !/^[a-z0-9_]+$/i.test(error)) return error;
  return fallback;
}

function unwrapData<T>(payload: Record<string, unknown>, fallback: string): T {
  if (payload.success === false) {
    throw new Error(extractMessage(payload, fallback));
  }
  if (payload.data !== undefined) return payload.data as T;
  return payload as T;
}

async function factoryFetch(path: string, init: RequestInit = {}, timeoutMs = 60_000): Promise<Record<string, unknown>> {
  assertFactoryApiTarget(appConfig.appFactoryBaseUrl);
  const token = await apiClient.getAccessToken();
  if (!token) throw new Error('未登录，无法开通家庭计划');

  const headers = new Headers(init.headers || {});
  headers.set('Accept', 'application/json');
  headers.set('Authorization', `Bearer ${token}`);
  if (init.body && !(init.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(`${appConfig.appFactoryBaseUrl}${path}`, {
      ...init,
      headers,
      signal: controller.signal,
    });
    const text = await res.text();
    let payload: Record<string, unknown> = {};
    try {
      payload = text ? (JSON.parse(text) as Record<string, unknown>) : {};
    } catch {
      payload = { message: text };
    }
    if (!res.ok) {
      throw new Error(extractMessage(payload, `家庭计划服务失败（HTTP ${res.status}）`));
    }
    if (payload.success === false) {
      throw new Error(extractMessage(payload, '家庭计划服务失败'));
    }
    return payload;
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error(`连接家庭计划支付超时（${appConfig.appFactoryBaseUrl}${path}）`);
    }
    if (error instanceof TypeError || /failed to fetch/i.test(String((error as Error)?.message || error))) {
      throw new Error(`无法连接 AppFactory ${appConfig.appFactoryBaseUrl}${path}`);
    }
    throw error;
  } finally {
    clearTimeout(timer);
  }
}

export const familyPlanService = {
  async loadCatalog(): Promise<FamilyPlanCatalog> {
    return apiClient.getEnvelope<FamilyPlanCatalog>('/api/family-plan/catalog');
  },

  async loadPricing(): Promise<FamilyPlanPricing> {
    const payload = await factoryFetch('/api/family-plan/pricing', { method: 'GET' });
    return unwrapData<FamilyPlanPricing>(payload, '加载家庭计划价格失败');
  },

  async listPlanners(): Promise<FamilyPlanPlanner[]> {
    const data = await apiClient.getEnvelope<{ planners?: FamilyPlanPlanner[] }>('/api/family-plan/planners');
    return data.planners || [];
  },

  async createOrder(input: { plannerUserId: string; subjects: string[] }): Promise<FamilyPlanOrder> {
    const payload = await factoryFetch('/api/family-plan/orders', {
      method: 'POST',
      body: JSON.stringify({
        plannerUserId: input.plannerUserId,
        subjects: input.subjects,
      }),
    });
    const data = unwrapData<{ order: FamilyPlanOrder }>(payload, '创建家庭计划失败');
    if (!data.order?.id) throw new Error('创建家庭计划失败');
    return data.order;
  },

  async listMyOrders(): Promise<FamilyPlanOrder[]> {
    const payload = await factoryFetch('/api/family-plan/orders/mine', { method: 'GET' });
    const data = unwrapData<{ orders?: FamilyPlanOrder[] }>(payload, '加载订单失败');
    return data.orders || [];
  },

  /** 网站走 Native 扫码付；小程序仍用 JSAPI（jsCode）。 */
  async createNativePay(orderId: string): Promise<FamilyPlanPayResult> {
    return factoryFetch(`/api/family-plan/orders/${encodeURIComponent(orderId)}/pay`, {
      method: 'POST',
      body: JSON.stringify({ channel: 'native' }),
    }) as Promise<FamilyPlanPayResult>;
  },

  async getPayStatus(orderId: string): Promise<FamilyPlanPayResult> {
    return factoryFetch(`/api/family-plan/orders/${encodeURIComponent(orderId)}/pay-status`, {
      method: 'GET',
    }) as Promise<FamilyPlanPayResult>;
  },

  async waitForPaid(orderId: string, attempts = 8, intervalMs = 1500): Promise<FamilyPlanPayResult> {
    let left = Math.max(1, attempts);
    let last: FamilyPlanPayResult = { success: true, paymentStatus: 'paying', orderId };
    while (left > 0) {
      last = await familyPlanService.getPayStatus(orderId);
      if (last.paymentStatus === 'paid' || last.alreadyPaid) return last;
      left -= 1;
      if (left <= 0) return last;
      await new Promise((resolve) => window.setTimeout(resolve, intervalMs));
    }
    return last;
  },
};
